import { useCallback, useEffect, useRef, useState } from 'react';
import type { RssItem } from '../lib/api.js';
import { fetchRssItems } from '../lib/api.js';
import {
  type DashboardResource,
  createDashboardResource,
  markResourceError,
  markResourceLoading,
  markResourceSuccess,
  updateResourceStaleness,
} from '../lib/dashboardResource.js';
import { useDashboard } from '../lib/store.js';

interface UseRssFeedOptions {
  pollIntervalMs?: number;
  staleAfterMs?: number;
}

interface UseRssFeedResult {
  rss: DashboardResource<RssItem[]>;
  refreshRss: () => Promise<void>;
}

export function useRssFeed({
  pollIntervalMs = 60_000,
  staleAfterMs = 90_000,
}: UseRssFeedOptions = {}): UseRssFeedResult {
  const { setRss } = useDashboard();
  const [rss, setRssResource] = useState(createDashboardResource<RssItem[]>());
  const mountedRef = useRef(true);

  const refreshRss = useCallback(async () => {
    if (!mountedRef.current) {
      return;
    }

    setRssResource((resource) => markResourceLoading(resource));
    try {
      const payload = await fetchRssItems({ tier: 0 });
      if (!mountedRef.current) {
        return;
      }
      setRss(payload);
      setRssResource((resource) =>
        markResourceSuccess(resource, payload, new Date().toISOString()),
      );
    } catch (error) {
      if (!mountedRef.current) {
        return;
      }
      setRssResource((resource) => markResourceError(resource, error));
    }
  }, [setRss]);

  useEffect(() => {
    mountedRef.current = true;
    refreshRss();
    const interval = setInterval(() => {
      refreshRss();
    }, pollIntervalMs);

    const freshness = setInterval(() => {
      setRssResource((resource) => updateResourceStaleness(resource, staleAfterMs));
    }, 1_000);

    return () => {
      mountedRef.current = false;
      clearInterval(interval);
      clearInterval(freshness);
    };
  }, [pollIntervalMs, staleAfterMs, refreshRss]);

  return { rss, refreshRss };
}
